import { Sparkles, ArrowRight, ExternalLink } from 'lucide-react'
import { GRADE_COLORS } from '@/lib/radar/scoring'
import { hasUsableScoreData } from '@/lib/radar/tool-scores'
import { sanitizeHref } from '@/lib/sanitize'
import { MiniStat } from './MiniStat'
import type { ToolDetailProps } from './detailRegistry'
import { PartialCoverageBanner } from './PartialCoverageBanner'

interface PromptSovViewData {
  grade: string | null
  overallScore: number | null
  scoreStatus?: 'graded' | 'withheld'
  url?: string
  aiSummary?: { summary?: string; actions?: string[] } | null
  scoreBreakdown?: {
    category: string
    score: number
    maxScore: number
    details?: string
    color?: string
  }[]
  targetSOV?: { sovPercent: number; rank: number; mentionCount?: number }
  totalBrandsFound?: number
  competitors?: { brand: string; rank: number; sovPercent: number }[]
  /** Score-eligible providers whose fan-out was attempted. */
  activeProviders?: string[]
  /** Attempted providers that returned usable, evaluated responses. */
  usableProviders?: string[]
  recommendations?: { priority: string; message: string }[]
}

/**
 * Dashboard detail renderer for `promptSov` (Phase 3, Step 3, Class D,
 * dashboard-only). Faithfully reproduces the legacy ToolDetailPanel rendering:
 * header (icon + name + grade pill + url), the AI Analysis block, score-breakdown
 * bars, the targetSOV MiniStats (Your SOV / Rank / Brands Found), the Competitor
 * SOV Rankings list (rank + brand + sovPercent + bar clamped at 100%, using
 * the intelligence color token), and recommendations.
 *
 * `usableProviders` is rendered via PartialCoverageBanner (Claims B-tier 2)
 * so partial provider coverage is disclosed; `activeProviders` remains the
 * attempted-provider set for compatibility and `promptCount`
 * remains unrendered. Grade comes from `data.grade` and is never recomputed.
 * Render-layer only.
 */
export function PromptSovDetail({
  data,
  toolIcon: Icon,
  toolColor,
  toolName,
}: ToolDetailProps) {
  const d = data as unknown as PromptSovViewData
  const withheld = !hasUsableScoreData(d) || d.grade == null
  const scoreColor = withheld ? 'var(--warning-text)' : GRADE_COLORS[d.grade!]
  const hasTarget = (d.targetSOV?.mentionCount || 0) > 0

  return (
    <div
      id='tool-detail-panel'
      className='rounded-xl border border-border bg-card p-5 space-y-5'
    >
      {/* Header */}
      <div className='flex items-baseline gap-3'>
        <span className='relative top-[3px] flex-shrink-0'>
          <Icon size={22} style={{ color: toolColor }} />
        </span>
        <h3 className='text-base font-bold text-foreground leading-none'>
          {toolName}
        </h3>
        <span
          className='text-sm font-bold px-2.5 py-1 rounded leading-none'
          style={{
            background: withheld
              ? 'color-mix(in srgb, var(--warning-text) 12%, transparent)'
              : `${scoreColor}15`,
            color: scoreColor,
          }}
        >
          {withheld ? 'Score withheld' : `${d.overallScore}/100`}
        </span>
        {d.url &&
          (() => {
            const safeUrl = sanitizeHref(d.url, {
              allowedSchemes: ['https:', 'http:'],
            })
            if (!safeUrl) return null
            return (
              <a
                href={safeUrl}
                target='_blank'
                rel='noopener noreferrer'
                className='ml-auto flex items-center gap-1 text-xs hover:underline text-muted-foreground'
              >
                {d.url} <ExternalLink size={9} />
              </a>
            )
          })()}
      </div>

      {/* Partial provider coverage disclosure */}
      {(d.usableProviders || d.activeProviders) && (
        <PartialCoverageBanner
          respondedProviders={d.usableProviders || d.activeProviders || []}
          expectedCount={
            d.usableProviders
              ? Math.max(1, d.activeProviders?.length || 4)
              : undefined
          }
          missingProviderReason='did not produce usable, evaluated responses during this scan'
        />
      )}

      {/* AI Summary */}
      {d.aiSummary?.summary && (
        <div
          className='rounded-lg border p-5'
          style={{
            borderColor: 'color-mix(in srgb, var(--primary) 12%, transparent)',
            background: 'color-mix(in srgb, var(--primary) 3%, transparent)',
          }}
        >
          <div className='flex items-center gap-2 mb-3'>
            <Sparkles size={14} style={{ color: 'var(--primary)' }} />
            <span
              className='text-xs font-bold uppercase tracking-wide'
              style={{ color: 'var(--primary)' }}
            >
              AI Analysis
            </span>
          </div>
          <p className='text-sm leading-relaxed text-foreground/90'>
            {d.aiSummary.summary}
          </p>
          {d.aiSummary.actions && d.aiSummary.actions.length > 0 && (
            <ul className='mt-4 space-y-2'>
              {d.aiSummary.actions.map((a, i) => (
                <li
                  key={i}
                  className='flex items-start gap-2 text-sm text-foreground/80'
                >
                  <ArrowRight
                    size={14}
                    className='mt-0.5 flex-shrink-0 text-primary'
                  />
                  {a}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Score breakdown */}
      {!withheld && d.scoreBreakdown && d.scoreBreakdown.length > 0 && (
        <div>
          <h4 className='text-xs font-bold mb-4 uppercase tracking-wide text-muted-foreground'>
            Score Breakdown
          </h4>
          <div className='space-y-4'>
            {d.scoreBreakdown.map(cat => (
              <div key={cat.category}>
                <div className='flex justify-between text-sm mb-1.5'>
                  <span className='text-foreground/90 font-medium'>
                    {cat.category}
                  </span>
                  <span className='font-mono font-bold text-foreground'>
                    {cat.score}/{cat.maxScore}
                  </span>
                </div>
                <div className='h-2 rounded-full bg-muted'>
                  <div
                    className='h-full rounded-full transition-all duration-500'
                    style={{
                      width: `${cat.maxScore > 0 ? (cat.score / cat.maxScore) * 100 : 0}%`,
                      background: cat.color || toolColor,
                    }}
                  />
                </div>
                {cat.details && (
                  <p className='text-xs mt-1.5 text-muted-foreground leading-relaxed'>
                    {cat.details}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Prompt SOV: rank + competitors */}
      {!withheld && d.targetSOV && (
        <div>
          <div className='grid grid-cols-3 gap-3 mb-4'>
            <MiniStat
              label='Your SOV'
              value={d.targetSOV.sovPercent}
              color={
                d.targetSOV.sovPercent >= 20
                  ? 'var(--pm-grade-a)'
                  : d.targetSOV.sovPercent > 0
                    ? 'var(--warning-text)'
                    : 'var(--pm-grade-f)'
              }
            />
            <MiniStat
              label='Rank'
              value={hasTarget ? d.targetSOV.rank : 'Not ranked'}
              color={
                !hasTarget
                  ? 'var(--pm-grade-f)'
                  : d.targetSOV.rank <= 3
                    ? 'var(--pm-grade-a)'
                    : d.targetSOV.rank <= 5
                      ? 'var(--warning-text)'
                      : 'var(--pm-grade-f)'
              }
            />
            <MiniStat label='Brands Found' value={d.totalBrandsFound || 0} />
          </div>
          {d.competitors && d.competitors.length > 0 && (
            <div>
              <h4 className='text-xs font-bold mb-3 uppercase tracking-wide text-muted-foreground'>
                Competitor SOV Rankings ({d.competitors.length})
              </h4>
              <div className='space-y-2'>
                {d.competitors.map(c => (
                  <div
                    key={c.brand}
                    className='flex items-center gap-3 text-sm text-foreground/80'
                  >
                    <span className='font-mono font-bold w-8 text-right text-muted-foreground'>
                      #{c.rank}
                    </span>
                    <span className='flex-1 truncate font-medium'>
                      {c.brand}
                    </span>
                    <span className='font-mono font-bold text-foreground'>
                      {c.sovPercent}%
                    </span>
                    <div className='w-24 h-2 rounded-full bg-muted'>
                      <div
                        className='h-full rounded-full'
                        style={{
                          width: `${Math.min(100, c.sovPercent)}%`,
                          background: 'var(--intelligence)',
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Recommendations */}
      {!withheld && d.recommendations && d.recommendations.length > 0 && (
        <div>
          <h4 className='text-xs font-bold mb-3 uppercase tracking-wide text-muted-foreground'>
            Recommendations ({d.recommendations.length})
          </h4>
          <div className='space-y-2.5'>
            {d.recommendations.map((rec, i) => (
              <div
                key={i}
                className='flex items-start gap-3 text-sm leading-relaxed'
              >
                <span
                  className='w-2 h-2 rounded-full mt-1.5 flex-shrink-0'
                  style={{
                    background:
                      rec.priority === 'high'
                        ? 'var(--pm-grade-f)'
                        : rec.priority === 'medium'
                          ? 'var(--warning-text)'
                          : 'var(--pm-grade-a)',
                  }}
                />
                <span className='text-foreground/85'>{rec.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
